import { CloseOutlined, DeleteOutlined } from '@ant-design/icons'
import { AppButton, AppSelectInput } from '@components/AppComponents'
import { AppTable } from '@components/table'
import OptionApi from '@services/OptionApi'
import { LOOKUP_CONFIG_TYPE } from '@utils/constants'
import { showNotificationSuccess } from '@utils/functions'
import { notification, Tabs } from 'antd'
import { useEffect, useState } from 'react'
import './EmlSpecialistProfileMappingConfig.scss'
import { ApiService } from '@services/ApiService'

interface LookupOption {
  id: string
  label: string
  displayName: string
}

interface SubSpecialty {
  eml: string
  mag: string[]
}

interface Specialty {
  eml: string
  mag: string[]
  subSpecialties?: SubSpecialty[]
}

interface Accreditation {
  code: string
  name: string
  description: string

  mag: string
}

interface MappingData {
  specialties: Specialty[]
  accreditations: Accreditation[]
}

const defaultMappingData: MappingData = {
  specialties: [
    {
      eml: 'Allergist/Immunologist',
      mag: [
        'Immunologist & Allergist',
        'Allergist',
        'Immunologist',
        'Immunologist & Allergist',
      ],
    },
    {
      eml: 'Anaesthetist',
      mag: ['Anaesthetist'],
      subSpecialties: [
        {
          eml: 'Ambulatory Anaesthesia',
          mag: [],
        },
        {
          eml: 'Anaesthetic Pharmacology',
          mag: [],
        },
        {
          eml: 'Oncologic Anaesthesia',
          mag: [],
        },
        {
          eml: 'Paediatric Anaesthesia',
          mag: [],
        },
        {
          eml: 'Acute Pain Medicine',
          mag: [],
        },
      ],
    },
    {
      eml: 'Cardiothoracic Surgeon',
      mag: ['Cardiothoracic Surgeon'],
    },
    {
      eml: 'Cardiologist',
      mag: ['Cardiologist'],
    },
    {
      eml: 'Chiropractor',
      mag: ['Chiropractor'],
    },
    {
      eml: 'Clinical Psychologist',
      mag: ['Clinical Psychologist'],
    },
    {
      eml: 'Colorectal Surgeon',
      mag: ['Colorectal Surgeon'],
      subSpecialties: [
        {
          eml: 'Bowel Cancer',
          mag: [],
        },
      ],
    },
    {
      eml: 'Consultant Surgeon',
      mag: [],
    },
    {
      eml: 'Dentist',
      mag: ['Dentist'],
    },
    {
      eml: 'Dermatologist',
      mag: ['Dermatologist'],
    },
    {
      eml: 'ENT Surgeon',
      mag: ['ENT Surgeon (Otolaryngologist = surgeries of the Head & Neck)'],
      subSpecialties: [
        {
          eml: 'Neck & Head Surgery',
          mag: [],
        },
        {
          eml: 'Hearing',
          mag: [],
        },
        {
          eml: 'Ear, Nose & Throat',
          mag: [],
        },
        {
          eml: 'Core',
          mag: [],
        },
      ],
    },
    {
      eml: 'Emergency Physician',
      mag: ['Accident & Emergency Physician'],
    },
    {
      eml: 'Endocrinologist',
      mag: ['Endocrinologist'],
      subSpecialties: [
        {
          eml: 'Diabetes',
          mag: [],
        },
        {
          eml: 'Urinary & Reproductive',
          mag: [],
        },
        {
          eml: 'Core',
          mag: [],
        },
        {
          eml: 'Digestive System',
          mag: [],
        },
        {
          eml: 'Endocrinology',
          mag: [],
        },
      ],
    },
    {
      eml: 'Gastroenterologist',
      mag: ['Gastroenterologist'],
    },
    {
      eml: 'General Physician',
      mag: ['Physician - General Medicine'],
    },
    {
      eml: 'General Practitioner',
      mag: ['General Practitioner'],
    },
    {
      eml: 'General Surgeon',
      mag: ['General Surgeon'],
    },
    {
      eml: 'Geriatrician',
      mag: ['Geriatrician'],
    },
    {
      eml: 'Gynaecologist',
      mag: [
        'Gynaecologist',
        'Obstetrician & Gynaecologist',
        'Uro-Gynaecologist & Gynaecologist',
      ],
    },
    {
      eml: 'Hand Surgeon',
      mag: ['Hand Surgeon'],
      subSpecialties: [
        {
          eml: 'Core',
          mag: [],
        },
        {
          eml: 'Upper Limb',
          mag: [],
        },
      ],
    },
    {
      eml: 'Immunologist',
      mag: [],
    },
    {
      eml: 'Injury Management Consultant (IMC)',
      mag: ['IMC Assessor'],
    },
    {
      eml: 'Intensivist',
      mag: ['Intensivist'],
    },
    {
      eml: 'Medical Practitioner',
      mag: [],
    },
    {
      eml: 'Musculoskeletal Medicine',
      mag: ['Musculo-Skeletal Medicine'],
    },
    {
      eml: 'Neonatologist',
      mag: ['Neonatologist'],
    },
    {
      eml: 'Nephrologist',
      mag: ['Nephrologist'],
    },
    {
      eml: 'Neurologist',
      mag: ['Neurologist'],
      subSpecialties: [
        {
          eml: 'Nervous System',
          mag: [],
        },
        {
          eml: 'Lower Limb',
          mag: [],
        },
        {
          eml: 'Upper Limb',
          mag: [],
        },
        {
          eml: 'Spine',
          mag: [],
        },
        {
          eml: 'Brain Injury',
          mag: [],
        },
      ],
    },
    {
      eml: 'Neuropsychologist',
      mag: ['Neuropsychologist'],
    },
    {
      eml: 'Neurosurgeon',
      mag: ['Neurosurgeon'],
      subSpecialties: [
        {
          eml: 'Spine',
          mag: ['Spine'],
        },
        {
          eml: 'core',
          mag: [],
        },
        {
          eml: 'Mental & Behavioural/Psych',
          mag: [],
        },
        {
          eml: 'Nervous System',
          mag: [],
        },
        {
          eml: 'Neurosurgery',
          mag: [],
        },
      ],
    },
    {
      eml: 'Obstetrician',
      mag: ['Obstetrician & Gynaecologist'],
    },
    {
      eml: 'Occupational Physician',
      mag: ['Occupational Physician'],
      subSpecialties: [
        {
          eml: 'Spine',
          mag: [],
        },
        {
          eml: 'Upper Limb',
          mag: [],
        },
        {
          eml: 'Orthopaedic and Musculoskeletal Medicine',
          mag: [],
        },
        {
          eml: 'Underwater Medicine',
          mag: [],
        },
        {
          eml: 'Military Medicine',
          mag: [],
        },
        {
          eml: 'Lower Limb',
          mag: [],
        },
        {
          eml: 'Occupational Medicine',
          mag: [],
        },
      ],
    },
    {
      eml: 'Occupational Therapist',
      mag: ['Occupational Therapist'],
    },
    {
      eml: 'Oncologist',
      mag: ['Oncologist'],
    },
    {
      eml: 'Ophthalmologist',
      mag: ['Ophthalmologist / Ophthalmic Surgeon'],
    },
    {
      eml: 'Oral & Maxillofacial Surgeon',
      mag: ['Oral & Maxillofacial Surgeon'],
    },
    {
      eml: 'Orthodontist',
      mag: ['Orthodontist'],
    },
    {
      eml: 'Orthopaedic Surgeon',
      mag: ['Orthopaedic Surgeon'],
      subSpecialties: [
        {
          eml: 'Spine',
          mag: ['Spine'],
        },
        {
          eml: 'Knee',
          mag: ['Knees'],
        },
        {
          eml: 'Shoulder',
          mag: ['Shoulders'],
        },
        {
          eml: 'Trauma',
          mag: ['Trauma'],
        },
        {
          eml: 'Ankle',
          mag: ['Ankles'],
        },
        {
          eml: 'Hip',
          mag: ['Hips'],
        },
        {
          eml: 'Upper Limb',
          mag: [],
        },
        {
          eml: 'Lower Limb',
          mag: [],
        },
        {
          eml: 'Elbow',
          mag: ['Elbows'],
        },
        {
          eml: 'Hand',
          mag: ['Hands'],
        },
        {
          eml: 'Core',
          mag: [],
        },
        {
          eml: 'General',
          mag: ['General'],
        },
        {
          eml: 'Foot',
          mag: ['Feet'],
        },
        {
          eml: 'Nervous System',
          mag: [],
        },
      ],
    },
    {
      eml: 'Paediatrician',
      mag: ['Paediatrician'],
    },
    {
      eml: 'Pain Specialist',
      mag: ['Pain Specialist'],
    },
    {
      eml: 'Palliative Medicine',
      mag: ['Palliative Medicine'],
    },
    {
      eml: 'Pathologist',
      mag: ['Pathologist'],
    },
    {
      eml: 'Pharmacist',
      mag: ['Pharmacist'],
    },
    {
      eml: 'Physiotherapist',
      mag: ['Physiotherapist'],
    },
    {
      eml: 'Physician',
      mag: [],
    },
    {
      eml: 'Plastic & General Reconstructive Surgeon',
      mag: ['Plastic & Reconstructive Surgeon'],
      subSpecialties: [
        {
          eml: 'Breast Surgery',
          mag: [],
        },
        {
          eml: 'Body Contour Surgery',
          mag: [],
        },
        {
          eml: 'Labioplasty',
          mag: [],
        },
        {
          eml: 'Facial Rejuvenation Surgery',
          mag: [],
        },
        {
          eml: 'Core',
          mag: [],
        },
        {
          eml: 'Upper Limb',
          mag: [],
        },
      ],
    },
    {
      eml: 'Podiatrist',
      mag: ['Podiatrist'],
    },
    {
      eml: 'Prosthodontist',
      mag: ['Prosthodontist'],
    },
    {
      eml: 'Psychiatrist',
      mag: ['Psychiatrist'],
      subSpecialties: [
        {
          eml: 'Forensic Psychiatry',
          mag: ['Forensic'],
        },
        {
          eml: 'Psychiatry',
          mag: [],
        },
        {
          eml: 'Core',
          mag: [],
        },
        {
          eml: 'Psychological & Psychiatric Disorders',
          mag: [],
        },
        {
          eml: 'Neuropsychiatry',
          mag: ['Neuropsychiatry'],
        },
        {
          eml: 'Mood Disorders',
          mag: [],
        },
      ],
    },
    {
      eml: 'Psychologist',
      mag: ['Psychologist'],
    },
    {
      eml: 'Radiologist',
      mag: ['Radiologist'],
    },
    {
      eml: 'Rehabilitation Physician',
      mag: ['Rehabilitation Physician'],
      subSpecialties: [
        {
          eml: 'Lower Limb',
          mag: [],
        },
        {
          eml: 'Nervous System',
          mag: [],
        },
        {
          eml: 'Dental (Minor)',
          mag: [],
        },
        {
          eml: 'Digestive System',
          mag: [],
        },
        {
          eml: 'Skin (Minor)',
          mag: [],
        },
        {
          eml: 'Spine',
          mag: [],
        },
        {
          eml: 'Upper Limb',
          mag: [],
        },
        {
          eml: 'Core',
          mag: [],
        },
        {
          eml: 'Brain',
          mag: [],
        },
      ],
    },
    {
      eml: 'Respiratory Physician',
      mag: ['Respiratory (Thoracic) & Sleep Physician'],
      subSpecialties: [
        {
          eml: 'Core',
          mag: [],
        },
        {
          eml: 'Respiratory System',
          mag: [],
        },
        {
          eml: 'Occupational Lung Disease',
          mag: [],
        },
        {
          eml: 'Chronic Obstructive Pulmonary Disease (COPD)',
          mag: [],
        },
      ],
    },
    {
      eml: 'Rheumatologist',
      mag: ['Rheumatologist'],
      subSpecialties: [
        {
          eml: 'Spine',
          mag: [],
        },
        {
          eml: 'Lower Limb',
          mag: [],
        },
        {
          eml: 'Upper Limb',
          mag: [],
        },
        {
          eml: 'Rheumatology',
          mag: [],
        },
        {
          eml: 'Overuse Syndromes',
          mag: [],
        },
        {
          eml: 'Neuropathic Pain Disorders',
          mag: [],
        },
        {
          eml: 'Shoulder',
          mag: [],
        },
        {
          eml: 'Neck',
          mag: [],
        },
        {
          eml: 'Core',
          mag: [],
        },
      ],
    },
    {
      eml: 'Sleep Physician',
      mag: ['Respiratory (Thoracic) & Sleep Physician'],
    },
    {
      eml: 'Speech Pathologist',
      mag: ['Speech Pathologist'],
    },
    {
      eml: 'Sports Physician',
      mag: ['Sport & Exercise Physician'],
    },
    {
      eml: 'Thoracic Medicine',
      mag: [],
    },
    {
      eml: 'Toxicologist',
      mag: ['Toxicologist'],
    },
    {
      eml: 'Urologist',
      mag: ['Urologist'],
    },
    {
      eml: 'Vascular Surgeon',
      mag: ['Vascular Surgeon'],
    },
  ],
  accreditations: [
    {
      code: 'AMA4',
      name: 'AMA 4',
      description:
        "American Medical Association's Guides to the Evaluation and Assessment of Permanent Impairment (4th Edition)",
      mag: 'AMA 4',
    },
    {
      code: 'AMA5',
      name: 'AMA 5',
      description:
        "American Medical Association's Guides to the Evaluation and Assessment of Permanent Impairment (5th Edition)",
      mag: 'AMA 5',
    },
    {
      code: 'AMA6',
      name: 'AMA 6',
      description:
        "American Medical Association's Guides to the Evaluation and Assessment of Permanent Impairment (6th Edition)",
      mag: 'AMA 6',
    },
    {
      code: 'CASA MRO',
      name: 'CASA MRO',
      description: 'CASA Medical Review officer',
      mag: '',
    },
    {
      code: 'Comcare PIA',
      name: 'Comcare PIA',
      description:
        'Comcare Permanent Impairment Assessor (Doctor has completed Comcare module for PI under SRC Act)',
      mag: 'COMCARE',
    },
    {
      code: 'DAME',
      name: 'DAME',
      description: 'Designated Aviation Medical Examiner, CASA',
      mag: '',
    },
    {
      code: 'DAO',
      name: 'DAO',
      description: 'Designated Aviation Opthamologist',
      mag: '',
    },
    {
      code: 'DPA (Optom)',
      name: 'DPA (Optom)',
      description:
        'Diagnostic Pharmaceutical Agent (DPA) certification for optometrists',
      mag: '',
    },
    {
      code: 'GARP',
      name: 'GARP',
      description:
        "DVA Guide to the Assessment of Rates of Veterans' Pensions (GARP)",
      mag: 'GARP',
    },
    {
      code: 'GEPI 2',
      name: 'GEPI 2',
      description:
        'Queensland Guidelines for Evaluation of Permanent Impairment - 2nd edition',
      mag: '',
    },
    {
      code: 'GEPIC',
      name: 'GEPIC',
      description:
        'Guide to the Evaluation of Psychiatric Impairment for Clinicians',
      mag: 'GEPIC',
    },
    {
      code: 'Life',
      name: 'Life (IP/TPD)',
      description:
        'Practitioner with experience performing assessments in life insurance industry (including income protection or lump sum)',
      mag: 'Life (IP/TPD)',
    },
    {
      code: 'LQMP',
      name: 'LQMP',
      description: 'Legally Qualified Medical Practitioner',
      mag: '',
    },
    {
      code: 'MAIAS AMP',
      name: 'MAIAS AMP',
      description:
        'Motor Accident Injury Accreditation Scheme (MAIAS) Accredited Medical Practitioner',
      mag: '',
    },
    {
      code: 'MAIAS ISV',
      name: 'MAIAS ISV',
      description:
        'Accredited Medical Practitioner who can perform Injury Scale Value (ISV) medical assessments through Motor Accident Injury Accreditation Scheme (MAIS)',
      mag: '',
    },
    {
      code: 'NSW PIC AMS',
      name: 'NSW PIC AMS',
      description:
        'Personal Injury Commission (NSW) appointed Approved Medical Specialist (AMS)',
      mag: 'AMS (NSW)',
    },
    {
      code: 'NSW PIC DRS',
      name: 'NSW PIC DRS Assessor',
      description: 'PIC NSW Dispute Resolution Service - Medical Assessor',
      mag: 'DRS (NSW)',
    },
    {
      code: 'PIRS',
      name: 'PIRS',
      description: 'Psychiatric Impairment Rating Scale PIRS',
      mag: 'PIRS',
    },
    {
      code: 'Rail AHP',
      name: 'Rail AHP',
      description: 'Rail Industry Worker - Authorised Health Professional',
      mag: '',
    },
    {
      code: 'RTWSA',
      name: 'RTWSA Trained',
      description: 'RTWSA Independent Medical Examiner Module',
      mag: '',
    },
    {
      code: 'RTWSA WPI',
      name: 'RTWSA Accredited WPI Assessor',
      description:
        'ReturntoWorkSA - Accredited Whole Person Impairment Assessor',
      mag: '',
    },
    {
      code: 'SAET IMA',
      name: 'SAET IMA',
      description:
        'South Australian Employment Tribunal Appointed Independent Medical Adviser (IMA)',
      mag: '',
    },
    {
      code: 'SIRA AHP',
      name: 'SIRA AHP',
      description: 'SIRA approved Allied Health Practitioner',
      mag: 'AHP (NSW)',
    },
    {
      code: 'SIRA approved IMC',
      name: 'SIRA approved IMC',
      description: 'SIRA approved Injury Management Consultant (IMC)',
      mag: 'IMC (NSW)',
    },
    {
      code: 'SIRA IME',
      name: 'SIRA IME',
      description: 'SIRA Independent Medical Examiner',
      mag: 'IME (NSW)',
    },
    {
      code: 'SIRA MAIA',
      name: 'SIRA Motor Accident Impairment Assessor',
      description:
        'SIRA Motor Accident Impairment Assessor authorised to assess PI in CTP scheme',
      mag: '',
    },
    {
      code: 'SIRA MAS Assessor',
      name: 'SIRA MAS Assessor',
      description:
        'Medical Assessor appointed under Motor Accidents Medical Assessment Service',
      mag: '',
    },
    {
      code: 'SIRA MAS DRS',
      name: 'SIRA MAS DRS',
      description:
        'SIRA Motor Accident Medical Assessment Service (MAS) - division of the Dispute Resolution Service (DRS) - assess motor accident claims',
      mag: '',
    },
    {
      code: 'SIRA PIA',
      name: 'SIRA PIA',
      description: 'SIRA Permanent Impairment assessor',
      mag: 'WIA (NSW)',
    },
    {
      code: 'TAC IIA',
      name: 'TAC IIA',
      description:
        'Transport Accident Commission (TAC) Independent Impairment Assessors',
      mag: 'IIA (TAC)',
    },
    {
      code: 'TAC IME',
      name: 'TAC IME',
      description:
        'Transport Accident Commission (TAC) Independent Medical Examiner',
      mag: 'IME (TAC)',
    },
    {
      code: 'TAC JME',
      name: 'TAC JME',
      description: 'Transport Accident Commission (TAC) Joint Medical Examiner',
      mag: 'TAC (JME)',
    },
    {
      code: 'TAS Acc',
      name: 'WorkCover TAS Accredited',
      description:
        'Practitioner accredited by WorkCover Tasmania to conduct IME',
      mag: '',
    },
    {
      code: 'TAS PI',
      name: 'TAS Accredited',
      description:
        'Practitioner accredited by WorkCover Board Tasmania to assess PI',
      mag: 'WPI (TAS)',
    },
    {
      code: 'WA APIA',
      name: 'WA APIA',
      description: 'Approved Permanent Impairment Assessor (APIA) WA',
      mag: 'APIA (WA)',
    },
    {
      code: 'WCQ IME',
      name: 'WorkSafe Qld IME Panel',
      description: 'WorkSafe Queensland IME panel member',
      mag: 'IME (QLD)',
    },
    {
      code: 'WCQ PI',
      name: 'WorkCover Qld PI Assessor',
      description:
        'Trained assessor in evaluation of permanent impairment (WorkSafe Qld)',
      mag: '',
    },
    {
      code: 'WCWA',
      name: 'WorkCover WA',
      description: 'WorkCover WA education module',
      mag: '',
    },
    {
      code: 'WSVic DP',
      name: 'VIC WorkSafe Dual Purpose Examiner',
      description: 'WorkSafe Vic Dual Purpose Examiner',
      mag: '',
    },
    {
      code: 'WSVic HLA',
      name: 'VIC WorkSafe HLA',
      description: 'WorkSafe Victoria Hearing Loss Assessor',
      mag: '',
    },
    {
      code: 'WSVic IIA',
      name: 'VIC WorkSafe IIA',
      description: 'WorkSafe Vic Independent Impairment Assessor',
      mag: 'IIA (VWA)',
    },
    {
      code: 'WSVic IME',
      name: 'VIC WorkSafe IME',
      description: 'WorkSafe Vic approved IME',
      mag: 'IME (VWA)',
    },
  ],
}

const EmlSpecialistProfileMappingConfig = () => {
  const [mappingData, setMappingData] =
    useState<MappingData>(defaultMappingData)
  const [specialtyOptions, setSpecialtyOptions] = useState<LookupOption[]>([])
  const [subSpecialtyOptions, setSubSpecialtyOptions] = useState<
    LookupOption[]
  >([])
  const [accreditationOptions, setAccreditationOptions] = useState<
    LookupOption[]
  >([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [activeTab, setActiveTab] = useState('1')

  // Helper function to check if a value exists in options
  const isValueInOptions = (value: string, options: LookupOption[]) => {
    if (!value) return true
    return options.some(opt => opt.displayName === value || opt.id === value)
  }

  const [isReady, setIsReady] = useState(false)
  // Fetch specialty and subspecialty options from API
  useEffect(() => {
    const fetchLookupConfigs = async () => {
      setLoading(true)
      try {
        const [specialtyRes, accreditationRes] = await Promise.all([
          OptionApi.LookupConfig({
            type: LOOKUP_CONFIG_TYPE.SPECIALTY,
            maxResultCount: 1000,
            isActive: true,
          }),

          OptionApi.LookupConfig({
            type: LOOKUP_CONFIG_TYPE.ACCREDITATION,
            maxResultCount: 1000,
            isActive: true,
          }),
        ])

        // const specialtyOptionsData = (specialtyRes.data.items || []).map(
        //   item => ({
        //     id: item.displayName,
        //     displayName: item.displayName,
        //   })
        // )

        // const subSpecialtyOptionsData = (subSpecialtyRes.data.items || []).map(
        //   item => ({
        //     id: item.displayName,
        //     displayName: item.displayName,
        //   })
        // )

        const accreditationOptionsData = (
          accreditationRes.data.items || []
        ).map(item => ({
          id: item.displayName,
          displayName: item.displayName,
        }))
        setSpecialtyOptions(specialtyRes?.data.items ?? [])
        // setSubSpecialtyOptions(subSpecialtyOptionsData)
        setAccreditationOptions(accreditationOptionsData)
      } finally {
        setLoading(false)
      }
    }

    fetchLookupConfigs()
  }, [])

  useEffect(() => {
    loadCurrentConfig()
  }, [])

  const loadCurrentConfig = async () => {
    const res = await ApiService.SystemConfig.GetSetting(
      'ICS.EML.SpecialistProfileMapping'
    )
    if (res?.data) {
      try {
        const mappingData = res?.data as MappingData
        if (!mappingData?.accreditations?.length) {
          mappingData.accreditations = defaultMappingData.accreditations
        }
        setMappingData(mappingData)
      } catch (error) {
        console.error(
          'Failed to parse EML Specialist Profile Mapping config:',
          error
        )
      }
    } else {
      setMappingData(defaultMappingData)
    }

    setIsReady(true)
  }
  // Handle adding a new MAG specialty row
  const handleAddMagSpecialty = (itemIndex: number) => {
    const newMappingData = { ...mappingData }
    newMappingData.specialties[itemIndex].mag.push('')
    setMappingData(newMappingData)
  }

  // Handle removing a MAG specialty row
  const handleRemoveMagSpecialty = (
    specialtyIndex: number,
    magIndex: number
  ) => {
    const newMappingData = { ...mappingData }
    newMappingData.specialties[specialtyIndex].mag.splice(magIndex, 1)
    setMappingData(newMappingData)
  }

  // Handle changing a MAG specialty value
  const handleChangeMagSpecialty = (
    specialtyIndex: number,
    magIndex: number,
    value: string
  ) => {
    const newMappingData = { ...mappingData }
    newMappingData.specialties[specialtyIndex].mag[magIndex] = value
    setMappingData(newMappingData)
  }

  // Handle changing a MAG subspecialty value
  const handleChangeMagSubSpecialty = (
    specialtyIndex: number,
    subSpecialtyIndex: number,
    value: string
  ) => {
    const newMappingData = { ...mappingData }
    if (newMappingData.specialties[specialtyIndex].subSpecialties) {
      newMappingData.specialties[specialtyIndex].subSpecialties![
        subSpecialtyIndex
      ].mag = [value]
    }
    setMappingData(newMappingData)
  }

  // Handle changing accreditation MAG value
  const handleChangeAccreditationMag = (
    accreditationIndex: number,
    value: string
  ) => {
    const newMappingData = { ...mappingData }
    newMappingData.accreditations[accreditationIndex].mag = value || ''
    setMappingData(newMappingData)
  }

  // Handle save (you can implement the actual save logic here)
  const handleSave = async () => {
    setSaving(true)
    try {
      await ApiService.SystemConfig.UpdateSystemConfig({
        'ICS.EML.SpecialistProfileMapping': JSON.stringify(mappingData),
      })
      showNotificationSuccess('Mapping configuration saved successfully')
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to save mapping configuration',
      })
      console.error('Failed to save mapping configuration:', error)
    } finally {
      setSaving(false)
    }
  }

  // Handle reset to default mapping
  const handleResetDefaultMapping = () => {
    setMappingData(defaultMappingData)
    notification.info({
      message: 'Default Mapping Loaded',
      description: 'Please click "Save Mapping" to save the configuration',
      duration: 5,
    })
  }

  // Handle re-sync all specialists
  const handleReSyncAllSpecialists = async () => {
    setSyncing(true)
    try {
      await ApiService.Eml.SyncAllSpecialists()
      showNotificationSuccess('All specialists synced successfully')
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to sync all specialists',
      })
      console.error('Failed to sync all specialists:', error)
    } finally {
      setSyncing(false)
    }
  }

  const specialtyColumns = [
    {
      name: 'emlSpecialty',
      title: 'EML Specialty',
      width: 250,
      Cell: (_, record) => {
        return record.eml
      },
    },
    {
      title: 'MAG - Specialty',
      dataIndex: 'magSpecialty',
      key: 'magSpecialty',
      width: 350,
      Cell: (_, record, recordIndex) => {
        return (
          <AppSelectInput
            mode="multiple"
            style={{ width: '100%' }}
            value={record.mag ?? []}
            options={specialtyOptions}
            valueKey="displayName"
            onChange={(values: string[]) => {
              const newMappingData = { ...mappingData }
              newMappingData.specialties[recordIndex].mag = values || []
              setMappingData(newMappingData)
            }}
            showSearch
            allowClear
            placeholder="Select MAG Specialties"
          />
        )
      },
    },
    {
      title: 'EML Expertise/Exclusion - MAG Sub-Specialty',
      width: 250,
      Cell: (_, record, recordIndex) => {
        const specialtyNames = record.mag

        const specialtyIds = specialtyOptions
          .filter(so => specialtyNames.includes(so.displayName))
          .map(so => so.id)

        return (
          <div>
            {record.subSpecialties?.map(
              (subItem: SubSpecialty, subSpecialtyIndex: number) => {
                // const isFound = isValueInOptions(
                //   subItem.mag[0],
                //   subSpecialtyOptions
                // )

                return (
                  <div className="flex gap-2 items-center py-2 border-b  border-solid border-b-grey-100">
                    <div className="w-[250px]">{subItem.eml}</div>
                    <div className="w-[250px]">
                      <AppSelectInput
                        source="LOOKUP_CONFIG"
                        sourceParams={{
                          type: 'SUB_SPECIALTY',
                          parentIds: specialtyIds,
                          isActive: true,
                        }}
                        allowClear
                        className="w-full mb-2"
                        value={subItem.mag[0]}
                        valueKey="displayName"
                        onChange={(value: string) => {
                          handleChangeMagSubSpecialty(
                            recordIndex,
                            subSpecialtyIndex,
                            value
                          )
                        }}
                        // style={{
                        //   ...(!isFound && subItem.mag[0]
                        //     ? { borderColor: 'red' }
                        //     : {}),
                        // }}
                        showSearch
                      />
                      {/* {!isFound && subItem.mag[0] && (
                        <span
                          style={{
                            color: 'red',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          (not found)
                        </span>
                      )} */}
                    </div>
                  </div>
                )
              }
            )}
          </div>
        )
      },
    },
  ]

  const accreditationColumns = [
    {
      name: 'code',
      title: 'Code',
      width: 150,
      Cell: (_, record) => {
        return record.code
      },
    },
    {
      name: 'name',
      title: 'Name',
      width: 200,
      Cell: (_, record) => {
        return record.name
      },
    },
    {
      name: 'description',
      title: 'Description',
      width: 300,
      Cell: (_, record) => {
        return record.description
      },
    },
    {
      name: 'magValue',
      title: 'MAG Value',
      width: 250,
      Cell: (_, record, recordIndex) => {
        const isFound = isValueInOptions(record.mag, accreditationOptions)
        return (
          <div>
            <AppSelectInput
              style={{
                width: '100%',
                ...(!isFound && record.mag ? { borderColor: 'red' } : {}),
              }}
              value={record.mag || ''}
              options={accreditationOptions}
              onChange={(value: string) => {
                handleChangeAccreditationMag(recordIndex, value)
              }}
              showSearch
              allowClear
            />
            {!isFound && record.mag && (
              <span
                style={{
                  color: 'red',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                }}
              >
                (not found)
              </span>
            )}
          </div>
        )
      },
    },
  ]

  if (!isReady) {
    return null
  }
  return (
    <div className="">
      <div className="flex justify-between">
        <h3>EML Specialist Profile Mapping Configuration</h3>
        <div className="flex gap-2">
          <AppButton color="default" onClick={handleResetDefaultMapping}>
            Reset default mapping
          </AppButton>
          <AppButton
            color="blue"
            onClick={handleReSyncAllSpecialists}
            loading={syncing}
          >
            Re-Sync All Specialists
          </AppButton>
          <AppButton color="green" onClick={handleSave} loading={saving}>
            Save Mapping
          </AppButton>
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <Tabs.TabPane tab="Specialties" key="1">
          <AppTable
            columns={specialtyColumns}
            data={mappingData.specialties}
            loading={loading}
            hasPaginate={false}
            totalCount={0}
            tableKey="eml-specialty-mapping-config"
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Accreditations" key="2">
          <AppTable
            columns={accreditationColumns}
            data={mappingData.accreditations}
            loading={loading}
            hasPaginate={false}
            totalCount={0}
            tableKey="eml-accreditation-mapping-config"
          />
        </Tabs.TabPane>
      </Tabs>
    </div>
  )
}

export default EmlSpecialistProfileMappingConfig
